import { motion } from 'framer-motion';

export interface CounterItem {
  key: string;
  label: string;
  icon?: string;
  min?: number;
  max?: number;
}

interface CounterListProps {
  items: CounterItem[];
  value: Record<string, number>;
  onChange: (value: Record<string, number>) => void;
  showTotal?: boolean;
  totalLabel?: (total: number) => string;
}

export default function CounterList({
  items,
  value,
  onChange,
  showTotal = true,
  totalLabel = (total) => `총 ${total}명과 함께 준비하시네요`,
}: CounterListProps) {
  const update = (item: CounterItem, delta: number) => {
    const current = value[item.key] || 0;
    const min = item.min ?? 0;
    const max = item.max ?? 99;
    const nextCount = Math.min(max, Math.max(min, current + delta));
    onChange({ ...value, [item.key]: nextCount });
  };

  const total = Object.values(value).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const count = value[item.key] || 0;
        const min = item.min ?? 0;
        const max = item.max ?? 99;

        return (
          <motion.div
            key={item.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.06, duration: 0.35 }}
            className="flex items-center justify-between rounded-2xl bg-white border border-cream-dark px-4 py-3.5"
          >
            <div className="flex items-center gap-3">
              {item.icon && (
                <div className="w-9 h-9 rounded-xl bg-navy/5 flex items-center justify-center">
                  <i className={`${item.icon} text-navy text-sm`} />
                </div>
              )}
              <span className="text-sm font-medium text-navy">{item.label}</span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => update(item, -1)}
                disabled={count <= min}
                className="w-8 h-8 rounded-full bg-cream-light border border-cream-dark flex items-center justify-center text-navy text-sm cursor-pointer active:scale-90 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label={`${item.label} 감소`}
              >
                <i className="ri-subtract-line" />
              </button>
              <span className="w-6 text-center text-sm font-semibold text-navy tabular-nums">
                {count}
              </span>
              <button
                type="button"
                onClick={() => update(item, 1)}
                disabled={count >= max}
                className="w-8 h-8 rounded-full bg-navy flex items-center justify-center text-white text-sm cursor-pointer active:scale-90 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label={`${item.label} 증가`}
              >
                <i className="ri-add-line" />
              </button>
            </div>
          </motion.div>
        );
      })}

      {showTotal && total > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center pt-2">
          <span className="text-xs text-navy-light">
            {totalLabel(total).split(String(total))[0]}
            <span className="font-semibold text-navy">{total}</span>
            {totalLabel(total).split(String(total)).slice(1).join(String(total))}
          </span>
        </motion.div>
      )}
    </div>
  );
}
